import { createContext, useContext, useEffect, useState } from "react";
import { useWebSocket } from "../Utilities/useWebSocket";
import { useCallback } from 'react';
import { markAllAsRead } from "../Pages/Inbox/utilities/markAllAsRead";
import { UserContext } from "./UserContext";
import getChatMessages from "../Pages/Inbox/utilities/getChatMessages";
import getChatSessions from "../Pages/Inbox/utilities/getChatSessions";

const WebSocketContext = createContext();

const testChatSessions = [
            {title:'NXTVid Studios Social Media', lastChatMessageSent:'NXTVid Submitted an RFP to Awesome Video Productions', _id:1, videographerId:'Cool Guy', clientId:'66e0dd1537b57962f9ff1c11', rfpId: 1, chatMessages:[{read:true}]},
            {title:'NXTVid Studios Social Media', lastChatMessageSent:'NXTVid Submitted an RFP to Awesome Video Productions', _id:2, videographerId:'66e0dd1537b57962f9ff1c11', clientId:'Cool Guy 2',  rfpId: 2, chatMessages:[{read:true}]},
            {title:'NXTVid Studios Social Media', lastChatMessageSent:'NXTVid Submitted an RFP to Awesome Video Productions', _id:3, videographerId:'Cool Guy 3', clientId:'66e0dd1537b57962f9ff1c11', rfpId: 3, chatMessages:[{read:true}]},
            ]

export const WebSocketProvider = ({ children }) => {
  const [chatSessions, setChatSessions] = useState(testChatSessions);
  const [chatMessages, setChatMessages] = useState([]);
  const [unreadMap, setUnreadMap] = useState({});
  const [selectedChatSession, setSelectedChatSession] = useState(null);
  const {userInfo} = useContext(UserContext)
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [typingMap, setTypingMap] = useState({});
  const [sessionTypingMap, setSessionTypingMap] = useState({});

  useEffect(() => {
          document.querySelectorAll('.unread-badge').forEach(el => el.remove());
          const fetchUnreadCounts = async () => {
          const newMap = {};
  
              await Promise.all(
              chatSessions.map(async (session) => {
                  try {
                  const messages = await getChatMessages(session._id);
  
                  const unreadMessages = messages.filter(
                      msg => msg.fromUserId !== userInfo._id && msg.status !== 'read'
                  );
  
                  newMap[session._id] = unreadMessages.length;
                  } catch (error) {
                  console.error(`Failed to get messages for session ${session._id}:`, error);
                  newMap[session._id] = 0;
                  }
              })
              );
  
              setUnreadMap(newMap);
              setTotalUnreadCount(Object.values(newMap).reduce((acc, count) => acc + count, 0));
          };
  
          if (chatSessions.length > 0 && userInfo?._id) {
              fetchUnreadCounts();
          }
          }, [chatSessions, userInfo._id]);

          const loadChatSessions = async () => {
            try {
            const sessions = await getChatSessions(userInfo._id);
            sessions.sort((a, b) => new Date(b.lastChatMessageSent) - new Date(a.lastChatMessageSent));
            setChatSessions(sessions);
            } catch (err) {
            console.error("Failed to load chat sessions:", err);
            }
        };
    
    
    useEffect(() => {
        if (!userInfo?._id) return;
        loadChatSessions();
    }, [userInfo._id]);

   


  const handleGlobalMessage = useCallback((event) => {
    const { type, payload } = JSON.parse(event.data);

    switch (type) {
      case "chat:message": {
        const { chatSessionId } = payload;
        if (selectedChatSession && chatSessionId === selectedChatSession._id) {
          const readPayload = {
            ...payload,
            read: true
          };
          setChatMessages(prev => [...prev, readPayload]);
          markAllAsRead(chatSessionId, userInfo?._id, setTotalUnreadCount)
        } else {
          setUnreadMap(prev => ({
            ...prev,
            [chatSessionId]: (prev[chatSessionId] || 0) + 1
          }));
          setTotalUnreadCount(prev => prev + 1);
        }
        break;
      }


      case 'chat:typing': {
  const { sessionId, userId, isTyping, isActive } = payload;

  // For the active chat window
  if (selectedChatSession && selectedChatSession._id === sessionId && isActive) {
    setTypingMap(prev => {
      const updated = { ...prev };
      if (isTyping) updated[userId] = true;
      else delete updated[userId];
      return updated;
    });
  }

  // For the session list
  setSessionTypingMap(prev => {
    const updated = { ...prev };

    if (isTyping) {
      if (!updated[sessionId]) updated[sessionId] = {};
      updated[sessionId][userId] = { startedAt: Date.now() };
    } else {
      if (updated[sessionId]) {
        delete updated[sessionId][userId];
        if (Object.keys(updated[sessionId]).length === 0) {
          delete updated[sessionId];
        }
      }
    }

    return updated;
  });

  break;
}




      default:
        console.warn("Unhandled socket message type:", type);
    }
  }, [selectedChatSession]);

  const { socket, sendMessage, isReady  } = useWebSocket(handleGlobalMessage);

  useEffect(() => {
  if (!chatSessions || !userInfo || !isReady) return;

  chatSessions.forEach(session =>
    sendMessage('chat:register', {
      userId: userInfo._id,
      sessionId: session._id,
    })
  );
}, [chatSessions, userInfo, isReady]);

 useEffect(() => {
    if (!socket || !isReady) return;
    const handler = (event) => {
      const { type } = JSON.parse(event.data);
      if (type === 'chat:message') {
        loadChatSessions(); // 👈 Run it on message
      }
    };
    socket.addEventListener('message', handler);
    return () => {
      socket.removeEventListener('message', handler);
    };
  }, [socket, isReady]);

  useEffect(() => {
  const interval = setInterval(() => {
    setSessionTypingMap(prev => {
      const now = Date.now();
      const updated = { ...prev };

      for (const sessionId in updated) {
        for (const userId in updated[sessionId]) {
          const startedAt = updated[sessionId][userId].startedAt;
          if (now - startedAt > 3000) {
            delete updated[sessionId][userId];
          }
        }

        if (Object.keys(updated[sessionId]).length === 0) {
          delete updated[sessionId];
        }
      }

      return updated;
    });
  }, 1000);

  return () => clearInterval(interval);
}, []);


  return (
    <WebSocketContext.Provider value={{ socket, sendMessage, isReady, chatMessages, setChatMessages, unreadMap, setUnreadMap, selectedChatSession, setSelectedChatSession, chatSessions, setChatSessions, totalUnreadCount, setTotalUnreadCount, typingMap, setTypingMap, sessionTypingMap }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useSocket = () => useContext(WebSocketContext);
